// components/ClanCrewQuestionCampaignModal.jsx
// Sprint: Push random sentiment questions to cohort users
// Integrated with real APIs
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Card, Select, Input, message, Spin, Checkbox, Empty, Tag, Tooltip, Alert, DatePicker } from 'antd';
import { LoadingOutlined, CalendarOutlined } from '@ant-design/icons';
import { Send, Users, HelpCircle, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import './clanCrewQuestionModal.css';
import {
    GET_LIST_OF_COHORTS,
    GET_User_Details_User_Management_For_ClanCrew,
    GET_SENTIMENT_QUESTION_BANK,
    GET_ALL_USER_PERSONA_QUESTIONS,
    CREATE_SENTIMENT_QUESTION_CAMPAIGN,
    ADD_COHORT_QUESTIONS_ASSIGNMENT
} from '../../services/userAPI';

const { Option } = Select;

const ClanCrewQuestionCampaignModal = ({
    visible,
    onCancel,
    accountId,
}) => {
    const location = useLocation();
    const roleId = location.state?.roleId;

    // State for cohort selection
    const [selectedCohortIds, setSelectedCohortIds] = useState([]);

    // State for user selection
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userSearchText, setUserSearchText] = useState('');
    const [usersByCohort, setUsersByCohort] = useState({}); // Map of cohortId -> users array
    const [currentFetchingCohortId, setCurrentFetchingCohortId] = useState(null); // Track which cohort is being fetched
    const [prevSelectedCohortIds, setPrevSelectedCohortIds] = useState([]); // Track previous selection to detect add/remove

    // State for question selection
    const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
    const [questionSearchText, setQuestionSearchText] = useState('');
    const [questionCategoryFilter, setQuestionCategoryFilter] = useState('');

    // State for confirmation step
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateError, setDateError] = useState('');

    // Fetch cohorts list
    const { data: cohortsData, loading: cohortsLoading } = useQuery(GET_LIST_OF_COHORTS, {
        variables: {
            accountId: accountId ? parseInt(accountId) : null,
            jobroleId: roleId ? parseInt(roleId) : null,
        },
        skip: !visible,
    });

    // Fetch questions
    const { data: questionsData, loading: questionsLoading } = useQuery(GET_ALL_USER_PERSONA_QUESTIONS, {
        variables: {
            searchTerm: "",
        },
        skip: !visible,
    });

    // Lazy query to fetch users based on cohort filters
    const [fetchUsers, { loading: usersLoading }] = useLazyQuery(GET_User_Details_User_Management_For_ClanCrew, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const fetchedUsers = data?.getUsersForAccount?.data || [];
            if (currentFetchingCohortId) {
                setUsersByCohort(prev => ({
                    ...prev,
                    [currentFetchingCohortId]: fetchedUsers,
                }));
            }
        },
    });


    // Add cohort questions assignment mutation
    const [addCohortQuestionsAssignment] = useMutation(ADD_COHORT_QUESTIONS_ASSIGNMENT);

    // Get cohorts from API
    const cohorts = useMemo(() => {
        return cohortsData?.getListOfCohorts || [];
    }, [cohortsData]);

    // Get questions from API
    const questions = useMemo(() => {
        return questionsData?.getAllUserPersonaQuestions || [];
    }, [questionsData]);

    // Users list from fetched data - combine users from all selected cohorts, avoiding duplicates
    const users = useMemo(() => {
        const allUsers = [];
        const seenIds = new Set();
        selectedCohortIds.forEach(cohortId => {
            const cohortUsers = usersByCohort[cohortId] || [];
            cohortUsers.forEach(user => {
                if (!seenIds.has(user.id)) {
                    seenIds.add(user.id);
                    allUsers.push(user);
                }
            });
        });
        return allUsers;
    }, [selectedCohortIds, usersByCohort]);

    // Filter users by search text
    const filteredUsers = useMemo(() => {
        let list = users;
        if (userSearchText) {
            list = list.filter(u =>
                u.name?.toLowerCase().includes(userSearchText.toLowerCase()) ||
                u.email?.toLowerCase().includes(userSearchText.toLowerCase())
            );
        }
        return list;
    }, [users, userSearchText]);

    // Filter questions by search and category
    const filteredQuestions = useMemo(() => {
        let list = questions;
        if (questionSearchText) {
            list = list.filter(q =>
                q.question?.toLowerCase().includes(questionSearchText.toLowerCase())
            );
        }
        if (questionCategoryFilter) {
            list = list.filter(q => q.category === questionCategoryFilter);
        }
        return list;
    }, [questions, questionSearchText, questionCategoryFilter]);

    // Get unique categories for filter dropdown
    const questionCategories = useMemo(() => {
        const cats = [...new Set(questions.map(q => q.category).filter(Boolean))];
        return cats.sort();
    }, [questions]);

    // Count active vs disabled users
    const activeUsers = useMemo(() => filteredUsers.filter(u => u.profileActivated), [filteredUsers]);
    const disabledUsers = useMemo(() => filteredUsers.filter(u => !u.profileActivated), [filteredUsers]);

    // Fetch users for a single cohort
    const fetchUsersForCohort = useCallback((cohortId) => {
        const cohort = cohorts.find(c => c.id === cohortId);
        if (!cohort) return;

        // Set current fetching cohort so onCompleted knows which cohort the data belongs to
        setCurrentFetchingCohortId(cohortId);

        // Parse filters from cohort - filters is stored as JSON array
        const cohortFilters = cohort.filters || [];
        const gqlFilters = [];

        // Convert cohort filters to API format
        if (Array.isArray(cohortFilters)) {
            cohortFilters.forEach(filter => {
                if (filter.field && filter.value) {
                    // Include all filters including unselectedUserIds
                    gqlFilters.push({ field: filter.field, value: filter.value });
                }
            });
        }

        // Fetch users with these filters
        fetchUsers({
            variables: {
                roleId: roleId ? parseInt(roleId) : null,
                filters: gqlFilters.length > 0 ? gqlFilters : null,
            },
        });
    }, [cohorts, roleId, fetchUsers]);

    // Handle cohort selection changes
    useEffect(() => {
        if (!visible) return;

        // Find newly added cohorts
        const addedCohortIds = selectedCohortIds.filter(id => !prevSelectedCohortIds.includes(id));
        // Find removed cohorts
        const removedCohortIds = prevSelectedCohortIds.filter(id => !selectedCohortIds.includes(id));

        // Remove users for deselected cohorts
        if (removedCohortIds.length > 0) {
            setUsersByCohort(prev => {
                const updated = { ...prev };
                removedCohortIds.forEach(id => {
                    delete updated[id];
                });
                return updated;
            });
        }

        // Fetch users for newly added cohorts (always fetch fresh, even if previously fetched)
        addedCohortIds.forEach(cohortId => {
            fetchUsersForCohort(cohortId);
        });

        // Update previous selection
        setPrevSelectedCohortIds(selectedCohortIds);

        // Clear user selection if all cohorts removed
        if (selectedCohortIds.length === 0) {
            setSelectedUserIds([]);
        }
    }, [selectedCohortIds, visible, prevSelectedCohortIds, fetchUsersForCohort]);

    // Auto-select all active users when users load
    useEffect(() => {
        if (users.length > 0) {
            const activeUserIds = users.filter(u => u.profileActivated).map(u => u.id);
            setSelectedUserIds(activeUserIds);
        }
    }, [users]);

    // Toggle user selection
    const toggleUser = (userId) => {
        const user = users.find(u => u.id === userId);
        if (!user?.profileActivated) {
            message.warning('Cannot select deactivated users');
            return;
        }
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    // Toggle all users
    const toggleAllUsers = () => {
        const activeUserIds = activeUsers.map(u => u.id);
        if (selectedUserIds.length === activeUserIds.length) {
            setSelectedUserIds([]);
        } else {
            setSelectedUserIds(activeUserIds);
        }
    };

    // Toggle question selection
    const toggleQuestion = (questionId) => {
        setSelectedQuestionIds(prev =>
            prev[0] === questionId ? [] : [questionId]
        );
    };

    // Toggle all questions
    const toggleAllQuestions = () => {
        const allQuestionIds = filteredQuestions.map(q => q.id);
        const allFilteredQuestionsSelected = allQuestionIds.every(id => selectedQuestionIds.includes(id));
        if (allFilteredQuestionsSelected) {
            setSelectedQuestionIds(prev => prev.filter(id => !allQuestionIds.includes(id)));
        } else {
            setSelectedQuestionIds(prev => [...new Set([...prev, ...allQuestionIds])]);
        }
    };

    // Reset form
    const resetForm = () => {
        setSelectedCohortIds([]);
        setSelectedUserIds([]);
        setSelectedQuestionIds([]);
        setUsersByCohort({});
        setCurrentFetchingCohortId(null);
        setPrevSelectedCohortIds([]);
        setUserSearchText('');
        setQuestionSearchText('');
        setQuestionCategoryFilter('');
        setShowConfirmation(false);
        setShowDatePicker(false);
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setShowDatePicker(false);
        setDateError('');
    };

    // Handle send - Real API call
    const handleSend = async () => {
        if (selectedQuestionIds.length === 0) {
            return message.error('Please select at least one question');
        }
        if (selectedCohortIds.length === 0) {
            return message.error('Please select at least one cohort');
        }
        if (!selectedDate) {
            setDateError('Please select a date to send the campaign');
            return;
        }

        setCreatingCampaign(true);
        setDateError('');

        try {
            // Calculate unselected user IDs (users who are active but not selected)
            const activeUserIds = users.filter(u => u.profileActivated).map(u => u.id);
            const unselectedUserIds = activeUserIds.filter(id => !selectedUserIds.includes(id));

            // Call the mutation for each selected cohort
            const promises = selectedCohortIds.map(cohortId =>
                addCohortQuestionsAssignment({
                    variables: {
                        cohortId: cohortId,
                        questionIds: selectedQuestionIds,
                        assignemntDate: selectedDate.format('YYYY-MM-DD'),
                        unselectedUserIds: unselectedUserIds.length > 0 ? unselectedUserIds : null,
                    },
                })
            );

            const responses = await Promise.all(promises);

            // Check if all requests were successful
            const allSuccessful = responses.every(
                response => response.data?.addCohortQuestionsAssignemnt?.success
            );

            if (allSuccessful) {
                message.success(
                    `Successfully assigned ${selectedQuestionIds.length} question(s) to ${selectedCohortIds.length} cohort(s) for ${selectedDate.format('MMM DD, YYYY')}`
                );
                resetForm();
                onCancel();
            } else {
                const failedResponses = responses.filter(
                    response => !response.data?.addCohortQuestionsAssignemnt?.success
                );
                message.error(
                    `Failed to assign questions to ${failedResponses.length} cohort(s). Please try again.`
                );
            }
        } catch (error) {
            console.error('Error assigning questions:', error);
            message.error('Failed to assign questions. Please try again.');
        } finally {
            setCreatingCampaign(false);
        }
    };

    // Get selected cohort names for display
    const selectedCohortNames = useMemo(() => {
        return cohorts
            .filter(c => selectedCohortIds.includes(c.id))
            .map(c => c.cohortName)
            .join(', ');
    }, [cohorts, selectedCohortIds]);

    // Get selected question texts for display
    const selectedQuestionTexts = useMemo(() => {
        return questions
            .filter(q => selectedQuestionIds.includes(q.id))
            .map(q => q.question);
    }, [questions, selectedQuestionIds]);

    return (
        <Modal
            open={visible}
            onCancel={() => { onCancel(); resetForm(); }}
            footer={null}
            width={1180}
            style={{ top: 12 }}
            className="clan-crew-question-modal"
            title={
                <div className="flex items-center gap-2">
                    <HelpCircle size={20} />
                    Push Sentiment Questions to Users
                </div>
            }
        >
            <Spin spinning={creatingCampaign || cohortsLoading || questionsLoading || usersLoading} indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD0101' }} spin />}>
                {!showConfirmation ? (
                    <div className="clan-crew-question-layout grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Left Panel - Cohorts & Users */}
                        <Card
                            title={
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <Users size={18} /> Cohorts & Users
                                    </span>
                                </div>
                            }
                            className="clan-crew-question-panel h-full"
                        >
                            {/* Cohort Selection */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">
                                    Select Cohorts <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    mode="multiple"
                                    showSearch
                                    placeholder="Select one or more cohorts"
                                    value={selectedCohortIds}
                                    onChange={setSelectedCohortIds}
                                    className="w-full"
                                    loading={cohortsLoading}
                                    allowClear
                                    tagRender={({ label, closable, onClose }) => (
                                        <span className="cohort-select-tag">
                                            {label}
                                            {closable && (
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${label}`}
                                                    onMouseDown={event => event.preventDefault()}
                                                    onClick={onClose}
                                                    className="cohort-select-tag-close"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </span>
                                    )}
                                    optionFilterProp="children" // important for label search
                                >
                                    {cohorts.map(c => (
                                        <Option key={c.id} value={c.id}>{c.cohortName}</Option>
                                    ))}
                                </Select>
                            </div>

                            {/* Selected Cohorts Filters */}
                            {/* {selectedCohortIds.length > 0 && (
                                <div className="mb-4 space-y-2">
                                    {cohorts
                                        .filter(c => selectedCohortIds.includes(c.id))
                                        .map(cohort => (
                                            <div key={cohort.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                <div className="font-medium text-sm text-blue-900 mb-1">
                                                    {cohort.cohortName}
                                                </div>
                                                {cohort.filters && Object.keys(cohort.filters).length > 0 ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {Object.entries(cohort.filters).map(([key, value]) => (
                                                            <span
                                                                key={key}
                                                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-800"
                                                            >
                                                                <span className="font-semibold">{key}:</span>
                                                                <span className="ml-1">{value}</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-gray-500 italic">No filters applied</div>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            )} */}

                            {/* User Stats */}
                            {selectedCohortIds.length > 0 && (
                                <div className="cohort-user-stats">
                                    {[
                                        ['Total', users.length],
                                        ['Active', activeUsers.length],
                                        ['Not Activated', disabledUsers.length],
                                        ['Selected', selectedUserIds.length],
                                    ].map(([label, count]) => (
                                        <div className="cohort-user-stat" key={label}>
                                            <span>{label}</span>
                                            <strong>{count}</strong>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* User Search */}
                            {selectedCohortIds.length > 0 && (
                                <div className="mb-3">
                                    <Input
                                        placeholder="Search users by name..."
                                        value={userSearchText}
                                        onChange={e => setUserSearchText(e.target.value)}
                                        allowClear
                                    />
                                </div>
                            )}

                            {/* Select All Button */}
                            {selectedCohortIds.length > 0 && activeUsers.length > 0 && (
                                <button
                                    onClick={toggleAllUsers}
                                    className="w-full py-2 mb-3 border border-gray-300 rounded hover:bg-gray-50 text-sm"
                                >
                                    {selectedUserIds.length === activeUsers.length ? 'Deselect All' : 'Select All Active Users'}
                                </button>
                            )}

                            {/* User List */}
                            <div className="max-h-48 overflow-y-auto space-y-1">
                                {selectedCohortIds.length === 0 ? (
                                    <Empty description="Select cohorts to see users" />
                                ) : filteredUsers.length === 0 ? (
                                    <Empty description="No users found" />
                                ) : (
                                    filteredUsers.map(user => {
                                        const isSelected = selectedUserIds.includes(user.id);
                                        const isDisabled = !user.profileActivated;
                                        return (
                                            <Tooltip
                                                key={user.id}
                                                title={isDisabled ? 'User is deactivated - cannot be selected' : ''}
                                            >
                                                <div
                                                    onClick={() => !isDisabled && toggleUser(user.id)}
                                                    className={`p-2 rounded-lg border transition ${isDisabled
                                                        ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                                                        : isSelected
                                                            ? 'border-[#AD0101] bg-red-50 cursor-pointer'
                                                            : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Checkbox
                                                            checked={isSelected}
                                                            disabled={isDisabled}
                                                            onChange={() => { }}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium truncate">{user.fullname}</div>
                                                            {/* <div className="text-xs text-gray-500 truncate">{user.email}</div> */}
                                                        </div>
                                                        {isDisabled && (
                                                            <Tag color="red" className="text-xs">Not Activated</Tag>
                                                        )}
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        );
                                    })
                                )}
                            </div>
                        </Card>

                        {/* Middle Panel - Question Bank */}
                        <Card
                            title={
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <HelpCircle size={18} /> Question Bank
                                    </span>
                                    <span className="text-sm font-normal text-gray-500">
                                        {selectedQuestionIds.length} selected
                                    </span>
                                </div>
                            }
                            className="clan-crew-question-panel h-full"
                        >
                            {/* Question Filters */}
                            <div className="space-y-2 mb-3">
                                <Input
                                    placeholder="Search questions..."
                                    value={questionSearchText}
                                    onChange={e => setQuestionSearchText(e.target.value)}
                                    allowClear
                                />
                                {/* <Select
                                    placeholder="Filter by category"
                                    value={questionCategoryFilter || undefined}
                                    onChange={v => setQuestionCategoryFilter(v || '')}
                                    className="w-full"
                                    allowClear
                                >
                                    {questionCategories.map(cat => (
                                        <Option key={cat} value={cat}>{cat}</Option>
                                    ))}
                                </Select> */}
                            </div>

                            {filteredQuestions.length > 0 && (
                                <button
                                    onClick={toggleAllQuestions}
                                    className="w-full py-2 mb-3 border border-[#AD0101] text-[#AD0101] rounded-md hover:bg-red-50 text-sm"
                                >
                                    {filteredQuestions.every(question => selectedQuestionIds.includes(question.id)) ? 'Deselect All' : 'Select All Questions'}
                                </button>
                            )}

                            {/* Question List */}
                            <div className="max-h-96 overflow-y-auto space-y-2">
                                {filteredQuestions.length === 0 ? (
                                    <Empty description="No questions available" />
                                ) : (
                                    filteredQuestions.map(question => {
                                        const isSelected = selectedQuestionIds.includes(question.id);
                                        return (
                                            <div
                                                key={question.id}
                                                onClick={() => toggleQuestion(question.id)}
                                                className={`p-2 rounded-lg border cursor-pointer transition ${isSelected
                                                    ? "border-[#AD0101] bg-red-50"
                                                    : "border-gray-200 hover:border-gray-300"
                                                    }`}
                                            >
                                                <div className="text-sm font-medium">
                                                    {question.question}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </Card>

                        {/* Right Panel - Summary & Actions */}
                        <Card
                            title={
                                <span className="flex items-center gap-2">
                                    <Send size={18} /> Confirmation Summary
                                </span>
                            }
                            className="clan-crew-question-panel h-full"
                        >
                            <div className="space-y-3">
                                {/* Summary Stats */}
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Cohorts:</span>
                                        <span className="font-medium">{selectedCohortIds.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Users:</span>
                                        <span className="font-medium text-[#AD0101]">{selectedUserIds.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Questions:</span>
                                        <span className="font-medium text-[#AD0101]">{selectedQuestionIds.length}</span>
                                    </div>
                                </div>

                                {/* Date Selection */}
                                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                    <div className="flex items-center gap-2 mb-2">
                                        {/* <Clock size={18} className="text-[#AD0101]" /> */}
                                        <span className="text-sm font-semibold text-gray-800">
                                            Schedule Send Date <span className="text-[#AD0101]">*</span>
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => setShowDatePicker(!showDatePicker)}
                                        className={`w-full py-2 px-3 rounded-lg border transition flex items-center justify-between ${dateError
                                            ? 'border-red-500 bg-white'
                                            : selectedDate
                                                ? 'border-gray-400 bg-white shadow-sm'
                                                : 'border-dashed border-gray-300 bg-white hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <CalendarOutlined className="text-gray-500" />
                                            <span className={selectedDate ? 'font-medium text-gray-900' : 'text-gray-500'}>
                                                {selectedDate ? selectedDate.format('MMM DD, YYYY') : 'Click to select date'}
                                            </span>
                                        </div>
                                        <span className="text-gray-500">▼</span>
                                    </button>

                                    {dateError && (
                                        <div className="mt-2 text-xs text-[#AD0101]">
                                            {dateError}
                                        </div>
                                    )}

                                    {showDatePicker && (
                                        <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200 shadow-lg">
                                            <DatePicker
                                                fullscreen={false}
                                                value={selectedDate}
                                                onChange={handleDateSelect}
                                                disabledDate={(current) => current && current < dayjs().startOf('day')}
                                                className="w-full"
                                            />
                                        </div>
                                    )}

                                    {selectedDate && !dateError && (
                                        <div className="mt-2 text-xs text-[#AD0101] bg-red-50 p-2 rounded">
                                            📅 Questions will be sent on <strong>{selectedDate.format('MMMM DD, YYYY')}</strong>
                                        </div>
                                    )}
                                </div>

                                {/* Selected Cohorts */}
                                {selectedCohortNames && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">Selected Cohorts:</div>
                                        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                                            {selectedCohortNames}
                                        </div>
                                    </div>
                                )}

                                {/* Selected Questions Preview */}
                                {selectedQuestionTexts.length > 0 && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">Selected Questions:</div>
                                        <div className="max-h-32 overflow-y-auto space-y-1">
                                            {selectedQuestionTexts.map((q, i) => (
                                                <div key={i} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                                    {i + 1}. {q}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Warnings */}
                                {disabledUsers.length > 0 && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        icon={<AlertTriangle size={16} />}
                                        message={`${disabledUsers.length} not activated users will be skipped`}
                                        className="text-xs"
                                    />
                                )}

                                <Alert
                                    type="info"
                                    showIcon
                                    message="Note"
                                    description="Users who have answered these questions within the last 30 days will be automatically skipped."
                                    className="mb-6"
                                />

                                {/* Confirmation Preview */}
                                {selectedUserIds.length > 0 && selectedQuestionIds.length > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                                        <CheckCircle size={20} className="mx-auto text-green-600 mb-2" />
                                        <div className="text-sm font-medium text-green-800">
                                            You're sending {selectedQuestionIds.length} questions to {selectedUserIds.length} users
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-2 pt-2">
                                    <button
                                        onClick={() => { onCancel(); resetForm(); }}
                                        className="flex-1 h-9 rounded-md border border-gray-300 font-medium hover:bg-gray-50 transition"
                                    >
                                        Cancel
                                    </button>
                                    {/* <button
                                        onClick={() => setShowConfirmation(true)}
                                        disabled={
                                            selectedUserIds.length === 0 ||
                                            selectedQuestionIds.length === 0 ||
                                            creatingCampaign
                                        }
                                        className={`flex-1 h-10 rounded-md text-white font-medium transition ${selectedUserIds.length === 0 ||
                                            selectedQuestionIds.length === 0 ||
                                            creatingCampaign
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-[#AD0101] hover:bg-[#b00000]'
                                            }`}
                                    >
                                        Review & Send
                                    </button> */}

                                    <button
                                        onClick={handleSend}
                                        disabled={creatingCampaign}
                                        className={`flex-1 h-9 rounded-md text-white font-medium transition ${creatingCampaign
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-[#AD0101] hover:bg-[#b00000]'
                                            }`}
                                    >
                                        {creatingCampaign ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : `Send to ${selectedUserIds.length} Users`}
                                    </button>
                                </div>
                            </div>
                        </Card>
                    </div>
                ) : (
                    /* Confirmation Step */
                    <div className="max-w-2xl mx-auto py-8">
                        <div className="text-center mb-8">
                            <Send size={48} className="mx-auto text-[#AD0101] mb-4" />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Campaign</h2>
                            <p className="text-gray-600">Please review the details before sending</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 mb-6 space-y-4">
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-gray-600">Cohorts:</span>
                                <span className="font-medium">{selectedCohortNames || 'None'}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-gray-600">Total Users:</span>
                                <span className="font-bold text-[#AD0101]">{selectedUserIds.length}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-gray-600">Questions to Send:</span>
                                <span className="font-bold text-[#AD0101]">{selectedQuestionIds.length}</span>
                            </div>
                            <div className="py-2">
                                <div className="text-gray-600 mb-2">Questions:</div>
                                <div className="space-y-2">
                                    {selectedQuestionTexts.map((q, i) => (
                                        <div key={i} className="text-sm bg-white p-2 rounded border">
                                            {i + 1}. {q}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <Alert
                            type="info"
                            showIcon
                            message="Note"
                            description="Users who have answered these questions within the last 3 months will be automatically skipped."
                            className="mb-6"
                        />

                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="flex-1 h-12 rounded-md border border-gray-300 font-medium hover:bg-gray-50 transition"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={creatingCampaign}
                                className={`flex-1 h-12 rounded-md text-white font-medium transition ${creatingCampaign
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#AD0101] hover:bg-[#b00000]'
                                    }`}
                            >
                                {creatingCampaign ? 'Sending...' : `Send to ${selectedUserIds.length} Users`}
                            </button>
                        </div>
                    </div>
                )}
            </Spin>
        </Modal>
    );
};

export default ClanCrewQuestionCampaignModal;
